/**
 * Owner-side reviews analytics widget — only renders when the venue's
 * plan has `can_access_review_analytics=true`. Backend gates the same way,
 * so this is a soft UI render-skip when the API returns `enabled: false`.
 *
 * Layout: 4 KPI tiles + 5★ distribution + tiny weekly trend sparkline +
 * language breakdown. Pure CSS bars, no chart library — keeps bundle slim.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Star, MessageSquare, TrendingUp, Globe2, Loader2, Lock } from "lucide-react";
import { API } from "../../App";


export default function OwnerReviewAnalytics() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/owner/reviews/analytics`, { params: { days: 90 } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ enabled: false }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!data) return null;

  if (!data.enabled) {
    return (
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3" data-testid="owner-analytics-locked">
        <Lock className="h-5 w-5 text-amber-700 dark:text-amber-400 mt-0.5" />
        <div>
          <p className="font-medium text-foreground">{t("owner:analytics_locked_title", { defaultValue: "Review analytics is a paid feature" })}</p>
          <p className="text-sm text-muted-foreground">
            {t("owner:analytics_locked_hint", { defaultValue: "Upgrade your plan to unlock weekly trends, reply rate and language breakdown." })}
          </p>
        </div>
      </div>
    );
  }

  return <AnalyticsBody data={data} />;
}


function AnalyticsBody({ data }) {
  const { t } = useTranslation();
  const total = data.total_reviews || 0;
  const dist = data.distribution || {};
  const maxBar = Math.max(1, ...Object.values(dist));
  const replied = data.reply_rate_pct ?? 0;
  const lang = data.language_breakdown || {};
  const series = data.timeseries || [];
  const peakCount = Math.max(1, ...series.map((b) => b.count));

  const kpis = useMemo(() => ([
    { label: t("owner:kpi_avg", { defaultValue: "Average rating" }), value: data.average_rating ? Number(data.average_rating).toFixed(1) : "—", icon: Star, accent: "text-yellow-500" },
    { label: t("owner:kpi_total", { defaultValue: "Reviews (90d)" }), value: total, icon: TrendingUp, accent: "text-emerald-600" },
    { label: t("owner:kpi_reply", { defaultValue: "Reply rate" }), value: `${replied}%`, icon: MessageSquare, accent: "text-primary" },
    { label: t("owner:kpi_langs", { defaultValue: "Languages" }), value: `${lang.en || 0} EN · ${lang.el || 0} EL`, icon: Globe2, accent: "text-violet-600" },
  ]), [data, total, replied, lang.en, lang.el, t]);

  return (
    <div className="space-y-4" data-testid="owner-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="bg-card rounded-xl border border-border p-4" data-testid={`owner-kpi-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground">{k.label}</p>
                <Icon className={`h-4 w-4 ${k.accent}`} />
              </div>
              <p className="text-2xl font-medium text-foreground tabular-nums">{k.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <p className="text-sm font-medium mb-3">{t("owner:analytics_distribution", { defaultValue: "Rating distribution" })}</p>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((s) => {
              const c = dist[s] || dist[String(s)] || 0;
              const pct = (c / maxBar) * 100;
              return (
                <div key={s} className="flex items-center gap-3 text-sm" data-testid={`owner-dist-row-${s}`}>
                  <span className="w-6 tabular-nums text-muted-foreground">{s}★</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 text-right tabular-nums text-xs text-muted-foreground">{c}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-4">
          <p className="text-sm font-medium mb-3">{t("owner:analytics_trend", { defaultValue: "Weekly volume (last 90 days)" })}</p>
          {series.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("owner:analytics_trend_empty", { defaultValue: "No reviews in the last 90 days." })}</p>
          ) : (
            <div className="flex items-end gap-1 h-24" data-testid="owner-trend-chart">
              {series.map((b) => {
                const h = (b.count / peakCount) * 100;
                return (
                  <div
                    key={b.week_starts_at}
                    className="flex-1 bg-primary/70 hover:bg-primary rounded-t transition-colors group relative"
                    style={{ height: `${Math.max(8, h)}%`, minWidth: 6 }}
                    title={`${b.week_starts_at} · ${b.count} reviews · avg ${b.avg ?? "—"}`}
                  >
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] tabular-nums text-muted-foreground opacity-0 group-hover:opacity-100">
                      {b.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            {series.length > 0 && `${series[0].week_starts_at} → ${series[series.length - 1].week_starts_at}`}
          </p>
        </div>
      </div>
    </div>
  );
}
